"use client";

import { useRef } from "react";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Backdrop + content wrapper classes. Default: "fixed inset-0 z-50 flex items-center justify-center bg-black/50" */
  className?: string;
};

/**
 * Modal wrapper that fixes the "text selection closes modal" bug:
 * When the user starts selecting text in an input, drags outside, and releases,
 * the modal no longer closes. We only close when both mousedown AND mouseup
 * occurred on the backdrop (i.e. an actual click on the backdrop).
 */
export function Modal({ isOpen, onClose, children, className }: ModalProps) {
  const mouseDownOnBackdrop = useRef(false);

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      mouseDownOnBackdrop.current = true;
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && mouseDownOnBackdrop.current) {
      onClose();
    }
    mouseDownOnBackdrop.current = false;
  };

  if (!isOpen) return null;

  return (
    <div
      className={className ?? "fixed inset-0 z-50 flex items-center justify-center bg-black/50"}
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}
    >
      {children}
    </div>
  );
}

type ModalContentProps = {
  children: React.ReactNode;
  className?: string;
  /** Optional extra attributes for the content div */
  onClick?: (e: React.MouseEvent) => void;
};

/**
 * Modal content wrapper. Clicks inside do not close the modal.
 * Use this for the inner card/panel that contains the modal content.
 */
export function ModalContent({ children, className, onClick }: ModalContentProps) {
  return (
    <div
      className={className}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
    >
      {children}
    </div>
  );
}
